import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Check, Copy, GitBranch, Info } from 'lucide-react';

interface WorkflowGeneratorDialogProps {
  open: boolean;
  onClose: () => void;
  appId: string;
}

type TriggerType = 'pr_merge' | 'schedule' | 'manual' | 'pr_open';
type ScheduleFrequency = 'daily' | 'weekly' | 'monthly' | 'custom';
type ScanType = 'delta' | 'complete';

const TRIGGER_OPTIONS: { value: TriggerType; label: string; description: string }[] = [
  { value: 'pr_merge', label: 'On PR Merge', description: 'Trigger when a PR is merged to the target branch' },
  { value: 'schedule', label: 'Scheduled', description: 'Run on a recurring schedule (cron)' },
  { value: 'manual', label: 'Manual', description: 'Trigger manually from GitHub Actions UI' },
  { value: 'pr_open', label: 'On PR Open', description: 'Run a pre-merge security check when a PR is opened' },
];

const SCHEDULE_OPTIONS: { value: ScheduleFrequency; label: string; cron: string }[] = [
  { value: 'daily', label: 'Daily (midnight UTC)', cron: '0 0 * * *' },
  { value: 'weekly', label: 'Weekly (Monday midnight UTC)', cron: '0 0 * * 1' },
  { value: 'monthly', label: 'Monthly (1st of month)', cron: '0 0 1 * *' },
  { value: 'custom', label: 'Custom cron', cron: '' },
];

function buildWorkflowYaml(
  trigger: TriggerType,
  scanType: ScanType,
  branch: string,
  scheduleFreq: ScheduleFrequency,
  customCron: string,
): string {
  const branches = branch.split(',').map(b => b.trim()).filter(Boolean);
  const branchList = branches.map(b => `${b}`).join(', ');

  let triggerSection = '';
  switch (trigger) {
    case 'pr_merge':
      triggerSection = `  push:\n    branches: [${branchList}]`;
      break;
    case 'schedule': {
      const cron = scheduleFreq === 'custom' ? customCron : SCHEDULE_OPTIONS.find(s => s.value === scheduleFreq)?.cron || '0 0 1 * *';
      triggerSection = `  schedule:\n    - cron: '${cron}'`;
      break;
    }
    case 'manual':
      triggerSection = `  workflow_dispatch:\n    inputs:\n      scan_type:\n        description: 'Scan type'\n        required: false\n        default: '${scanType}'\n        type: choice\n        options:\n          - delta\n          - complete`;
      break;
    case 'pr_open':
      triggerSection = `  pull_request:\n    branches: [${branchList}]\n    types: [opened, synchronize, reopened]`;
      break;
  }

  return `name: Sentinel Security Scan

on:
${triggerSection}

jobs:
  sentinel-scan:
    runs-on: ubuntu-latest
    steps:
      - name: Trigger Sentinel Scan
        env:
          SENTINEL_API_URL: \${{ secrets.SENTINEL_API_URL }}
          SENTINEL_APP_ID: \${{ secrets.SENTINEL_APP_ID }}
          SENTINEL_API_KEY: \${{ secrets.SENTINEL_API_KEY }}
        run: |
          echo "🔒 Triggering Sentinel security scan..."
          
          # Extract repo name from github.repository (owner/repo-name -> repo-name)
          REPO_NAME=$(echo "\${{ github.repository }}" | cut -d'/' -f2)
          echo "Repository: \${REPO_NAME}"
          
          API_URL=$(echo "\${SENTINEL_API_URL}" | xargs | sed 's|/v1/extract$||' | sed 's|/$||')
          SCAN_TYPE="${trigger === 'manual' ? '${{ github.event.inputs.scan_type || \'' + scanType + '\' }}' : scanType}"
          
          RESPONSE=$(curl -s -w "\\n%{http_code}" -X POST \\
            "\${API_URL}/v1/extract" \\
            -H "Content-Type: application/json" \\
            -d "{
              \\"appId\\": \\"\${SENTINEL_APP_ID}\\",
              \\"apiKey\\": \\"\${SENTINEL_API_KEY}\\",
              \\"scanType\\": \\"\${SCAN_TYPE}\\",
              \\"repos\\": [\\"\${REPO_NAME}\\"]
            }")
          
          HTTP_CODE=$(echo "$RESPONSE" | tail -1)
          BODY=$(echo "$RESPONSE" | head -n -1)
          
          echo "Response: $BODY"
          echo "Status: $HTTP_CODE"
          
          if [ "$HTTP_CODE" -ne 200 ] && [ "$HTTP_CODE" -ne 202 ]; then
            echo "::error::Sentinel scan trigger failed with status $HTTP_CODE"
            exit 1
          fi
          
          echo "✅ Sentinel scan triggered successfully for \${REPO_NAME}"
`;
}

export default function WorkflowGeneratorDialog({ open, onClose, appId }: WorkflowGeneratorDialogProps) {
  const theme = useTheme();
  const [trigger, setTrigger] = useState<TriggerType>('pr_merge');
  const [scanType, setScanType] = useState<ScanType>('complete');
  const [branch, setBranch] = useState('main');
  const [scheduleFreq, setScheduleFreq] = useState<ScheduleFrequency>('monthly');
  const [customCron, setCustomCron] = useState('0 0 1 * *');
  const [copied, setCopied] = useState(false);

  const yaml = buildWorkflowYaml(trigger, scanType, branch, scheduleFreq, customCron);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(yaml);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const el = document.createElement('textarea');
      el.value = yaml;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), display: 'flex' }}>
            <GitBranch size={22} color={theme.palette.primary.main} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} fontSize={16}>Integrate with Pipeline</Typography>
            <Typography variant="body2" color="text.secondary" fontSize={12}>
              Generate a GitHub Actions workflow to trigger Sentinel scans automatically
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {/* Trigger Type */}
          <FormControl>
            <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Trigger</FormLabel>
            <RadioGroup value={trigger} onChange={(e) => setTrigger(e.target.value as TriggerType)}>
              {TRIGGER_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={
                    <Stack>
                      <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{opt.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{opt.description}</Typography>
                    </Stack>
                  }
                  sx={{ mb: 0.5, alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.25 } }}
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Schedule frequency (only for schedule trigger) */}
          {trigger === 'schedule' && (
            <FormControl>
              <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>Frequency</FormLabel>
              <RadioGroup row value={scheduleFreq} onChange={(e) => setScheduleFreq(e.target.value as ScheduleFrequency)}>
                {SCHEDULE_OPTIONS.map(opt => (
                  <FormControlLabel
                    key={opt.value}
                    value={opt.value}
                    control={<Radio size="small" />}
                    label={<Typography sx={{ fontSize: 12 }}>{opt.label}</Typography>}
                  />
                ))}
              </RadioGroup>
              {scheduleFreq === 'custom' && (
                <TextField
                  size="small"
                  label="Cron expression"
                  value={customCron}
                  onChange={(e) => setCustomCron(e.target.value)}
                  placeholder="0 0 * * 1"
                  helperText="minute hour day-of-month month day-of-week (UTC)"
                  sx={{ mt: 1, maxWidth: 300 }}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                />
              )}
            </FormControl>
          )}

          {/* Branch + Scan Type (side by side) */}
          <Stack direction="row" spacing={2}>
            {(trigger === 'pr_merge' || trigger === 'pr_open') && (
              <TextField
                size="small"
                label="Branch(es)"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="main, develop"
                helperText="Comma-separated branch names"
                sx={{ flex: 1 }}
                InputProps={{ sx: { fontSize: 13 } }}
              />
            )}
            <FormControl sx={{ minWidth: 180 }}>
              <FormLabel sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>Scan Type</FormLabel>
              <RadioGroup row value={scanType} onChange={(e) => setScanType(e.target.value as ScanType)}>
                <FormControlLabel value="delta" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Delta (fast)</Typography>} />
                <FormControlLabel value="complete" control={<Radio size="small" />} label={<Typography sx={{ fontSize: 12 }}>Complete (full)</Typography>} />
              </RadioGroup>
            </FormControl>
          </Stack>

          {/* YAML Preview */}
          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Generated Workflow</Typography>
              <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                <Button
                  size="small"
                  variant={copied ? 'contained' : 'outlined'}
                  color={copied ? 'success' : 'primary'}
                  startIcon={copied ? <Check size={14} /> : <Copy size={14} />}
                  onClick={handleCopy}
                  sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12 }}
                >
                  {copied ? 'Copied!' : 'Copy Workflow'}
                </Button>
              </Tooltip>
            </Stack>
            <Box
              sx={{
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)',
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                p: 2,
                maxHeight: 320,
                overflow: 'auto',
              }}
            >
              <pre style={{ margin: 0, fontFamily: '"JetBrains Mono", "Fira Code", monospace', fontSize: 11.5, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                {yaml}
              </pre>
            </Box>
          </Box>

          {/* Instructions */}
          <Alert severity="info" icon={<Info size={18} />} sx={{ '& .MuiAlert-message': { fontSize: 12.5 } }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>Setup Instructions</Typography>
            <ol style={{ margin: 0, paddingLeft: 18 }}>
              <li>Save the workflow as <code>.github/workflows/sentinel-scan.yml</code> in your repository</li>
              <li>Add these <strong>repository secrets</strong> in your Git repo settings:
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, mb: 0.5 }} flexWrap="wrap">
                  <Chip label="SENTINEL_API_URL" size="small" sx={{ fontFamily: 'monospace', fontSize: 10.5, height: 22 }} />
                  <Chip label="SENTINEL_APP_ID" size="small" sx={{ fontFamily: 'monospace', fontSize: 10.5, height: 22 }} />
                  <Chip label="SENTINEL_API_KEY" size="small" sx={{ fontFamily: 'monospace', fontSize: 10.5, height: 22 }} />
                </Stack>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                  <code>SENTINEL_API_URL</code> = base URL only, e.g. <code>https://sentinel-api.your-domain.com</code> (no <code>/v1/extract</code> path)
                </Typography>
              </li>
              <li>Create an API key in <strong>Settings → API Subscription</strong> with the <strong>Sentinel</strong> scope</li>
              <li>Your App ID is: <Chip label={appId} size="small" sx={{ fontFamily: 'monospace', fontSize: 11, height: 20, fontWeight: 600 }} /></li>
            </ol>
          </Alert>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
